import React, { useState, useMemo, useEffect } from 'react';
import { Box, Select, Chip, MenuItem, IconButton, Link as MuiLink, createTheme, ThemeProvider, FormControl, InputLabel, Popover, Checkbox, FormControlLabel, Button, TextField, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { ExpandLess, ExpandMore, LocationOn } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ruRU } from '@mui/x-data-grid/locales';
import { DataGrid, GridToolbar, GridToolbarQuickFilter, GridToolbarColumnsButton } from '@mui/x-data-grid';


// Создаем тему с русской локализацией
const theme = createTheme(
  {
    palette: {
      primary: { main: '#1976d2' },
    },
  },
  ruRU,
);

const statusLabels = {
  ONLINE: "В сети",
  WARN: "Не в сети",
  PROBLEM: "С ошибками",
};

const ADDRESS_COLLATOR = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });


// Компонент для выпадающих чекбоксов
function CheckboxDropdown({ label, options, selectedValues, onChange, getLabel, getValue }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value) => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      const allValues = options.map(opt => getValue ? getValue(opt) : opt);
      onChange(allValues);
    }
  };

  const getOptionValue = (option) => {
    return getValue ? getValue(option) : option;
  };

  const getOptionLabel = (option) => {
    return getLabel ? getLabel(option) : (typeof option === 'object' ? option.name : option);
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        size="small"
        sx={{ minWidth: 110, justifyContent: 'space-between', height: '40px' }}
      >
        {selectedValues.length === 0
          ? label
          : `${label} (${selectedValues.length})`
        }
        ▼
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedValues.length === options.length}
                indeterminate={selectedValues.length > 0 && selectedValues.length < options.length}
                onChange={handleSelectAll}
              />
            }
            label="Выбрать все"
          />
          <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1, pt: 1, display: 'flex', flexDirection: 'column' }}>
            {options.map((option) => {
              const optionValue = getOptionValue(option);
              return (
                <FormControlLabel
                  key={optionValue}
                  control={
                    <Checkbox
                      checked={selectedValues.includes(optionValue)}
                      onChange={() => handleToggle(optionValue)}
                    />
                  }
                  label={getOptionLabel(option)}
                />
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}


function CustomToolbar({ statusFilter, setStatusFilter, allStatuses, typeFilter, setTypeFilter, allType, groupFilter, setGroupFilter, allGroup }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '0.5rem 1rem',
        backgroundColor: 'white',
        borderBottom: '1px solid #f0f0f0',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: 'center' }}>
        <GridToolbarQuickFilter
          debounceMs={500}
          sx={{
            minWidth: 400,
            paddingBottom: 0,
            '& .MuiInputBase-root': {
              backgroundColor: 'transparent',
              borderRadius: '4px',
              padding: '4px 12px',
              border: '1px solid rgba(25, 118, 210, 0.5)',
              transition: 'all 0.2s ease-in-out',
              height: '40px',
              '&:hover': {
                border: '1px solid #1976d2',
              },
              '&.Mui-focused': {
                border: '2px solid rgba(25, 118, 210, 0.5)',
                padding: '3px 11px',
              },
              '&::before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },

            },
            '& .MuiInputBase-input': {
              fontSize: '14px',
              fontWeight: 500,
            },
            '& .MuiSvgIcon-root': {
              color: '#1976d2',
            },

          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: 'center' }} >
        <CheckboxDropdown
          label="Группа"
          options={allGroup}
          selectedValues={groupFilter}
          onChange={setGroupFilter}
          getValue={(group) => group.id}
          getLabel={(group) => group.name || ''}
        />
        <CheckboxDropdown
          label="Статус"
          options={allStatuses}
          selectedValues={statusFilter}
          onChange={setStatusFilter}
          getLabel={(status) => statusLabels[status] || status}
        />

        <CheckboxDropdown
          label="Тип"
          options={allType}
          selectedValues={typeFilter}
          onChange={setTypeFilter}
        />
        <GridToolbarColumnsButton
          sx={{ height: '40px' }}
        />
      </Box>



    </Box >
  );
}


const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-root': {
    border: 'none',
    overflow: 'hidden !important',
  },
  '& .MuiDataGrid-main': {
    overflow: 'hidden !important',
  },
  '& .MuiDataGrid-virtualScroller': {
    overflow: 'hidden !important',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    overflow: 'hidden !important',
  },
  '& .MuiDataGrid-virtualScrollerContent--overflowed': {
    overflow: 'hidden !important',
  },
  '& .MuiDataGrid-virtualScrollerRenderZone': {
    overflow: 'hidden !important',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: 'white',
  },
  '& .MuiDataGrid-row': {
    backgroundColor: 'white',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e0e0e0',
  },
  '& .MuiDataGrid-footerContainer': {
    backgroundColor: 'white',
    borderTop: '1px solid #f0f0f0',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const StatusChip = ({ status }) => {

  return (
    <Box sx={{ p: 2, display: "flex", gap: 1 }}>
      <img src={`/${status.toLowerCase()}.svg`} alt="" />
      <img src="/lock.svg" alt="lock" style={{ width: 16, height: 16 }} />
    </Box>
  );
};

const DeviceNumberCell = ({ device }) => {
  const dev_number = device?.device_number || 'N/A';
  return <Box>{dev_number}</Box>;
}

const DeviceEuiCell = ({ device }) => {
  const devEui = device?.module_number || 'N/A';
  return <Box>{devEui}</Box>;
}

const DevTypeValue = (device_type, device_resource_type) => {
  if (device_type === "ВС" && device_resource_type === "Горячая вода") {
    return "ГВС";
  } else if (device_type === "ВС" && device_resource_type === "Холодная вода") {
    return "ХВС";
  } else if (device_type === "ТС") {
    return "ТС";
  } else if (device_type === "ЭС") {
    return "ЭС";
  } else if (device_type === "ГС") {
    return "ГС";
  }else {
    return "ВС";
  }
}

const DeviceTypeCell = ({ device_type }) => {

  if (!device_type) {
    return <Box>N/A</Box>;
  }

  const getTypeDisplay = () => {
    if (device_type === "ГВС") {
      return { text: "ГВС", color: "red" };
    } else if (device_type === "ХВС") {
      return { text: "ХВС", color: "blue" };
    } else if (device_type === "ТС") {
      return { text: "ТС", color: "default" };
    } else if (device_type === "ЭС") {
      return { text: "ЭС", color: "default" };
    } else if (device_type === "ГС") {
      return { text: "ГС", color: "default" };
    } else {
      return { text: "ВС", color: "blue" };
    }
  };

  const { text, color } = getTypeDisplay();

  return (
    <Box color={color === 'red' ? 'error.main' : color === 'blue' ? 'primary.main' : 'text.primary'}>
      {text}
    </Box>
  );
};

const AddressValue = (device) => {

  const fullAddress = [
    device?.address_city,
    device?.address_street,
    device?.address_building,
    device?.address_number
  ].filter(Boolean).join(', ');

  return fullAddress;
}

const AddressCell = (address) => {

  return <Box>{address}</Box>;
};

const VolumeData = (device) => {
  const last_volume = device?.data_last_volume;
  const start_volume = device?.data_start_volume;
  const factor = device?.device_factor;
  const device_type = device?.type;
  const totalVolume = (parseFloat((last_volume || 0) + (start_volume || 0)) * (factor || 1)).toFixed(3);
  return parseFloat(totalVolume).toFixed(3);
}

const LastReadingCell = ({ volume, device_type }) => {

  if (!volume) {
    return <Box>N/A</Box>;
  }

  const unit = device_type === 'ГВС' ? 'м³' : device_type === 'ХВС' ? 'м³' : device_type === 'ТС' ? 'ГДж' : device_type === 'ГС' ? 'м³' : 'кВт-Ч';

  return <Box>{`${volume} ${unit}`}</Box>;
};


const DateCell = ({ lastDateTime }) => {
  const formatDate = (dateTime) => {
    if (dateTime === 0) {
      return "Нет данных";
    }
    return new Date(dateTime * 1000).toLocaleDateString('ru-RU');
  };

  return <Box>{formatDate(lastDateTime)}</Box>;
};

const ActionsCell = ({ id }) => {
  return (
    <IconButton component={Link} to={`/device/${id}`} size="small">
      <img src="/document.svg" alt="details" style={{ width: 16, height: 16 }} />
    </IconButton>
  );
};

const DevicesTable = ({ devicesList, loading = false, searchAddressQuery, user }) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    status: true,
    type: true,
    device_number: true,
    deveui: false,
    address: true,
    volume: true,
    date: true,
    actions: true,
  });
  
  const allGroup = useMemo(() => {
    const csGroups = (user.group || []).map((d) => ({ id: d.id || d._id, name: d.name }));
    const nbiotGroups = (user.group || []).map((d) => ({ id: d.id || d._id, name: d.name }));
    
    const allGroupsMap = new Map();
    [...csGroups, ...nbiotGroups].forEach(group => {
      if (group.id && !allGroupsMap.has(group.id)) {
        allGroupsMap.set(group.id, group);
      }
    });
    
    const allGroupsArray = Array.from(allGroupsMap.values());
    return allGroupsArray.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB, 'ru');
    });
  }, [user])


  const allStatuses = useMemo(() => {
    return [...new Set(devicesList.map((d) => d.data_status).filter(Boolean))];
  }, [devicesList]);

  const allType = useMemo(() => {
    return [...new Set(devicesList.map((d) => DevTypeValue(d.device_type, d.device_resource_type)).filter(Boolean))];
  }, [devicesList]);

  const [statusFilter, setStatusFilter] = useState([]);
  const [groupFilter, setGroupFilter] = useState([])
  const [typeFilter, setTypeFilter] = useState([]);

  // Обновляем фильтры при изменении данных
  useEffect(() => {
    if (allStatuses.length > 0) {
      setStatusFilter(allStatuses);
    }
  }, [allStatuses]);

  useEffect(() => {
    if (allType.length > 0) {
      setTypeFilter(allType);
    }
  }, [allType]);

  useEffect(() => {
    if (allGroup.length > 0) {
      setGroupFilter(allGroup.map(g => g.id));
    }
  }, [allGroup]);



  const columns = useMemo(() => [
    {
      field: 'status',
      headerName: 'Статус',
      flex: 1,
      maxWidth: 100,
      renderCell: (params) => <StatusChip status={params?.row?.status} />,
      sortable: true,
      getApplyQuickFilterFn: () => null, // Исключаем из поиска
    },
    {
      field: 'type',
      headerName: 'Тип',
      flex: 1,
      maxWidth: 100,
      sortable: true,
      renderCell: (params) => <DeviceTypeCell device_type={params?.row?.type} />,
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'device_number',
      headerName: 'Заводской номер',
      flex: 1,
      sortable: true,
      maxWidth: 150,
    },
    {
      field: 'deveui',
      headerName: 'DevEUI/Imei',
      flex: 1,
      maxWidth: 150,
    },
    {
      field: 'address',
      headerName: 'Адрес',
      flex: 1,
      minWidth: 300,
      sortable: true,
      sortComparator: (v1, v2) => ADDRESS_COLLATOR.compare(String(v1 || ''), String(v2 || '')),
    },
    {
      field: 'volume',
      headerName: 'Последние показания',
      flex: 1,
      maxWidth: 170,
      renderCell: (params) => <LastReadingCell volume={params?.row?.volume} device_type={params?.row?.type} />,
      sortable: true,
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'date',
      headerName: 'Дата',
      flex: 1,
      maxWidth: 130,
      renderCell: (params) => <DateCell lastDateTime={params?.row?.date} />,
      sortable: true,
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'id',
      headerName: 'Действия',
      flex: 1,
      maxWidth: 100,
      renderCell: (params) => <ActionsCell id={params?.row?.id} />,
      sortable: false,
      disableColumnMenu: true,
      getApplyQuickFilterFn: () => null,
    },
  ], []);

  const rows = useMemo(() => {
    if (!devicesList) return [];

    let filtered = devicesList;

    if (statusFilter.length > 0 && statusFilter.length < allStatuses.length) {
      filtered = filtered.filter(row => statusFilter.includes(row.data_status));
      console.log('After status filter:', filtered.length);
    }

    if (typeFilter.length > 0 && typeFilter.length < allType.length) {
      filtered = filtered.filter(row => typeFilter.includes(DevTypeValue(row.device_type, row.device_resource_type)));
      console.log('After type filter:', filtered.length);
    }

    if (groupFilter.length > 0 && groupFilter.length < allGroup.length) {
      filtered = filtered.filter(device => {
        const deviceGroupId = device.group;
        if (!deviceGroupId) return false;
        const groupId = typeof deviceGroupId === 'object' ? (deviceGroupId.id || deviceGroupId._id) : deviceGroupId;
        return groupFilter.includes(groupId);
      });
      console.log('After group filter:', filtered.length);
    }

    if (searchAddressQuery && searchAddressQuery.length > 0) {
      
      const query = searchAddressQuery.join(', ').toLowerCase();
      console.log('Applying address search filter with query:', query);
      filtered = filtered.filter(device => {
        const fullAddress = AddressValue(device).toLowerCase();
        return fullAddress.includes(query);
      });
      console.log('After address search filter:', filtered.length);
    }

    const data = filtered.map((device, index) => {

      return {
        id: device._id,
        status: device.data_status,
        type: DevTypeValue(device.device_type, device.device_resource_type),
        address: AddressValue(device),
        device_number: device.device_number,
        deveui: device.device_iot_module_number,
        volume: VolumeData(device),
        group: device.group,
        date: device.data_last_date_time,
        paid_ownership: device.paid_ownership,
        paid_type_ownership: device.paid_type_ownership,
        city: device.address_city,
        street: device.address_street,
        building: device.address_building,
      };
    });
    
    return data;
  }, [devicesList, statusFilter, typeFilter, groupFilter, searchAddressQuery]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        height: "auto",
        gridRow: '2',
        gridColumn: '1 / -1',
        margin: '10px'
      }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          slots={{
            toolbar: () => (
              <CustomToolbar
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                allStatuses={allStatuses}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                allType={allType}
                groupFilter={groupFilter}
                allGroup={allGroup}
                setGroupFilter={setGroupFilter}
              />
            ),
          }}
          loading={loading}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          disableRowSelectionOnClick
          disableColumnFilter
          disableDensitySelector
          disableColumnMenu
          disableVirtualization={true}
          hideFooter={false}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          checkboxSelection={false}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-root': {
              overflow: 'hidden !important',
            },
            '& .MuiDataGrid-main': {
              overflow: 'hidden !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'hidden !important',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: 'white !important',
              borderTop: '1px solid #f0f0f0',
            },
            '& .MuiTablePagination-root': {
              backgroundColor: 'white !important',
            },
            '& .MuiDataGrid-selectedRowCount': {
              backgroundColor: 'white !important',
            },
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default DevicesTable;
